import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { pageColors } from '../pages/HomePage';
import AccountMenu from './AccountMenu';
import controlDeckLogo from '../assets/controldeckLogo.png';
import controlDeckNavbarMark from '../assets/controldeckNavbarMark.png';

const brandColors = {
  ink: '#10212b',
  border: 'rgba(16,33,43,0.09)',
};

const navGroups = [
  {
    key: 'operations',
    label: 'Operations',
    items: [
      { label: 'Orders', path: '/orders', color: pageColors.orders, requiresTenantContext: true },
      {
        label: 'Purchase Order',
        path: '/purchase-orders',
        color: pageColors.purchaseOrders,
        requiresTenantContext: true,
      },
      {
        label: 'Supplier Invoices',
        path: '/supplier-invoices',
        color: pageColors.supplierInvoices,
        requiresTenantContext: true,
      },
    ],
  },
  {
    key: 'sales-reports',
    label: 'Sales & reports',
    items: [
      {
        label: 'Article Margin Report',
        path: '/articles',
        color: pageColors.articles,
        requiresTenantContext: true,
      },
      {
        label: 'Purchase support',
        path: '/sales-trends',
        color: pageColors.summary,
        requiresTenantContext: true,
      },
      {
        label: 'Customer Sales Report',
        path: '/customer-order-matrix',
        color: pageColors.customerOrderMatrix,
        requiresTenantContext: true,
      },
      {
        label: 'Financial Report',
        path: '/statistics',
        color: pageColors.statistics,
        requiresTenantContext: true,
      },
    ],
  },
  {
    key: 'crm',
    label: 'CRM',
    items: [
      {
        label: 'Customer Details',
        path: '/customers',
        color: pageColors.customers,
        requiresTenantContext: true,
      },
      {
        label: 'Customer Sales Trends',
        path: '/customer-sales-trends',
        color: pageColors.customerSales,
        requiresTenantContext: true,
      },
      {
        label: 'Activity Planner',
        path: '/crm',
        color: pageColors.crm,
        requiresTenantContext: true,
      },
      {
        label: 'Activity Overview',
        path: '/crm/stats',
        color: pageColors.crmStats,
        requiresTenantContext: true,
        allowedRoles: ['TENANT_ADMIN', 'SUPER_ADMIN'],
      },
    ],
  },
  {
    key: 'administration',
    label: 'Administration',
    items: [
      {
        label: 'Users',
        path: '/users',
        color: pageColors.users,
        requiresTenantContext: false,
        allowedRoles: ['TENANT_ADMIN', 'SUPER_ADMIN'],
      },
    ],
  },
];

const crmNavPaths = new Set(['/crm', '/crm/stats', '/customers']);

function isNavItemActive(pathname, itemPath) {
  if (itemPath === '/crm') {
    return pathname === '/crm';
  }

  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

export default function NavBar({
  crmEnabled,
  currentUser,
  onLogout,
  supplierInvoicesEnabled,
  tenantContextRequired,
}) {
  const [openMenu, setOpenMenu] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const useCompactNavbarWidth = ['/profile', '/invoices'].includes(location.pathname);
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  const visibleNavGroups = useMemo(
    () =>
      navGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (crmEnabled || !crmNavPaths.has(item.path)) &&
              (supplierInvoicesEnabled || item.path !== '/supplier-invoices') &&
              (!item.allowedRoles || item.allowedRoles.includes(currentUser?.role))
          ),
        }))
        .filter((group) => group.items.length > 0),
    [crmEnabled, currentUser?.role, supplierInvoicesEnabled]
  );

  const cacheStatusDisabled = Boolean(tenantContextRequired);

  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  const handleMenuOpen = (event, groupKey) => {
    setOpenMenu({ anchorEl: event.currentTarget, groupKey });
  };

  const handleMenuClose = () => {
    setOpenMenu(null);
  };

  return (
    <>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          boxShadow: '0 8px 28px rgba(16,33,43,0.06)',
          borderBottom: `1px solid ${brandColors.border}`,
        }}
      >
        <Container maxWidth={useCompactNavbarWidth ? 'lg' : 'xl'}>
          <Toolbar
            disableGutters
            sx={{
              display: { xs: 'flex', md: 'grid' },
              gridTemplateColumns: { md: '1fr auto 1fr' },
              justifyContent: { xs: 'space-between' },
              alignItems: 'center',
              gap: 2,
              py: isMobile ? 1.25 : 1.5,
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ minWidth: 0, justifySelf: { md: 'start' } }}
            >
              <Box
                component={Link}
                to="/"
                aria-label="Go to ControlDeck home"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: '0 0 auto',
                }}
              >
                <Box
                  component="img"
                  src={isMobile ? controlDeckNavbarMark : controlDeckLogo}
                  alt="ControlDeck"
                  sx={{
                    width: isMobile ? 48 : { md: 190, lg: 220 },
                    height: isMobile ? 48 : 42,
                    objectFit: 'contain',
                    objectPosition: 'left center',
                  }}
                />
              </Box>
            </Stack>

            {!isMobile && (
              <Box
                component="nav"
                aria-label="Primary navigation"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { md: 2, lg: 3 },
                  minWidth: 0,
                  justifySelf: 'center',
                }}
              >
                {visibleNavGroups.map((group) => {
                  const isOpen = openMenu?.groupKey === group.key;
                  const isActive = group.items.some((item) =>
                    isNavItemActive(location.pathname, item.path)
                  );

                  return (
                    <React.Fragment key={group.key}>
                      <Button
                        id={`${group.key}-menu-button`}
                        aria-controls={isOpen ? `${group.key}-menu` : undefined}
                        aria-haspopup="true"
                        aria-expanded={isOpen ? 'true' : undefined}
                        onClick={(event) => handleMenuOpen(event, group.key)}
                        endIcon={
                          <KeyboardArrowDownRoundedIcon
                            sx={{
                              transform: isOpen ? 'rotate(180deg)' : 'none',
                              transition: 'transform 160ms ease',
                            }}
                          />
                        }
                        sx={{
                          minHeight: 42,
                          borderRadius: 2.5,
                          px: 1.6,
                          textTransform: 'none',
                          fontSize: '0.94rem',
                          fontWeight: isActive || isOpen ? 700 : 500,
                          color: isActive || isOpen ? '#0f766e' : brandColors.ink,
                          backgroundColor:
                            isActive || isOpen ? 'rgba(15,118,110,0.08)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(15,118,110,0.08)',
                          },
                        }}
                      >
                        {group.label}
                      </Button>

                      <Menu
                          id={`${group.key}-menu`}
                          anchorEl={isOpen ? openMenu.anchorEl : null}
                          open={isOpen}
                          onClose={handleMenuClose}
                          MenuListProps={{
                            'aria-labelledby': `${group.key}-menu-button`,
                            sx: { py: 1 },
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          slotProps={{
                            paper: {
                              elevation: 0,
                              sx: {
                                mt: 1,
                                minWidth: 290,
                                maxWidth: 340,
                                borderRadius: 3,
                                border: `1px solid ${brandColors.border}`,
                                boxShadow: '0 20px 48px rgba(16,33,43,0.16)',
                                overflow: 'hidden',
                              },
                            },
                          }}
                        >
                          {group.items.map((item) => {
                            const isItemActive = isNavItemActive(location.pathname, item.path);
                            const isDisabled = Boolean(
                              item.requiresTenantContext && tenantContextRequired
                            );

                            return (
                              <MenuItem
                                key={item.path}
                                component={isDisabled ? 'button' : Link}
                                to={isDisabled ? undefined : item.path}
                                selected={isItemActive}
                                disabled={isDisabled}
                                onClick={isDisabled ? undefined : handleMenuClose}
                                sx={{
                                  mx: 1,
                                  minHeight: 48,
                                  borderRadius: 2,
                                  gap: 1.25,
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(15,118,110,0.09)',
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(15,118,110,0.13)',
                                  },
                                }}
                              >
                                <Box
                                  aria-hidden="true"
                                  sx={{
                                    width: 4,
                                    height: 26,
                                    flex: '0 0 auto',
                                    borderRadius: '999px',
                                    backgroundColor: item.color,
                                    opacity: isDisabled ? 0.45 : 1,
                                  }}
                                />
                                <ListItemText
                                  primary={item.label}
                                  primaryTypographyProps={{
                                    fontWeight: isItemActive ? 700 : 500,
                                    color: brandColors.ink,
                                  }}
                                />
                              </MenuItem>
                            );
                          })}
                      </Menu>
                    </React.Fragment>
                  );
                })}
              </Box>
            )}

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ minWidth: 0, justifySelf: { md: 'end' } }}
            >
              <AccountMenu
                currentUser={currentUser}
                onLogout={onLogout}
                showTenantSettingsLink={isSuperAdmin && isMobile}
                showCacheStatusLink={isSuperAdmin}
                cacheStatusDisabled={cacheStatusDisabled}
                buttonSx={{
                  flexShrink: 0,
                  borderColor: brandColors.border,
                  backgroundColor: '#fff',
                  px: { xs: 0.5, sm: 1.1 },
                  py: { xs: 0.5, sm: 0.75 },
                  '& .MuiAvatar-root': { width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 } },
                }}
              />
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
}
